<template>
	<div class="attachment">
		<div class="file-icon">
			<IconSvg :icon="fileIcon" />
		</div>

		<template v-if="attachment.error">
			<span class="attachment-title" :title="attachment.filename">
				{{ attachment.filename }}
			</span>
			<span class="error">
				<IconSvg icon="alert" />
				<span>{{ attachment.error }}</span>
			</span>

			<ToolTip class="info" text="Dit bestand zal niet worden toegevoegd">
				<IconSvg icon="information" />
			</ToolTip>
		</template>

		<template v-else>
			<a
					v-if="attachment.download"
					class="attachment-title"
					href="#"
					:title="attachment.filename"
					@click.prevent="emit('download')"
			>
				{{ attachment.filename }}
			</a>
			<span
					v-else
					class="attachment-title"
					:title="attachment.filename"
			>
				{{ attachment.filename }}
			</span>

			<span
					v-if="upload && (upload?.tag === 'uploading' || upload?.tag === 'uploaded') "
					class="status"
					:class="upload?.tag === 'uploading' ? 'status-progress' : 'status-completed'"
			>
				<span class="bar"></span>
				{{ upload?.tag === 'uploaded' ? '100%' : '' }}
			</span>

			<button
					v-if="attachment.delete && !readonly"
					type="button"
					@click="emit('delete')"
			>
				<IconSvg icon="delete" />
				<span class="sr-only">verwijderen</span>
			</button>
		</template>
	</div>
</template>

<script lang="ts" setup>
	import type {PropType} from 'vue';
	import {computed} from 'vue';
	import {getIconForMimeType} from '~/utils/fileUploadHelper';
	import IconSvg from '~/components/generic/IconSvg.vue';
	import type {ClaimedUpload, UnclaimedUpload} from '~/lib/types/uploads';
	import type {UploadState} from '~/composables/uploads/UploadDialog.vue';
	import ToolTip from '~/components/generic/ToolTip.vue';

	const props = defineProps({
		claimedAttachment: {
			type: Object as PropType<ClaimedUpload>,
			required: false,
			default: undefined
		},
		unclaimedAttachment: {
			type: Object as PropType<UnclaimedUpload>,
			required: false,
			default: undefined
		},
		upload: {
			type: Object as PropType<UploadState>,
			required: false,
			default: undefined
		},
		readonly: {
			type: Boolean,
			default: false
		},
		downloadable: {
			type: Boolean,
			default: true
		}
	});

	const fileIcon = computed(() => {
		if (props.claimedAttachment) {
			return getIconForMimeType(props.claimedAttachment.contentType);
		}
		if (props.unclaimedAttachment) {
			return getIconForMimeType(props.unclaimedAttachment.contentType);
		}

		if (props.upload && props.upload.tag === 'uploading') {
			return getIconForMimeType(props.upload.file.type);
		}

		if (props.upload && props.upload.tag === 'uploaded') {
			return getIconForMimeType(props.upload.upload.contentType);
		}
		return getIconForMimeType(undefined);
	});

	const emit = defineEmits(['download', 'delete']);

	type Attachment = {
		filename: string;
		contentType: string | undefined;
		download: boolean,
		delete: boolean,
		error?: string
	}

	const attachment = computed<Attachment>(() => {
		if (props.claimedAttachment) {
			return {
				filename: props.claimedAttachment.filename,
				contentType: props.claimedAttachment.contentType,
				download: props.downloadable,
				delete: !props.readonly
			};
		}
		if (props.unclaimedAttachment) {
			return {
				filename: props.unclaimedAttachment.filename,
				contentType: props.unclaimedAttachment.contentType,
				download: props.downloadable,
				delete: !props.readonly
			};
		}

		if (props.upload && props.upload.tag === 'uploading') {
			return {
				filename: props.upload.file.name,
				contentType: props.upload.file.type,
				download: props.downloadable,
				delete: false
			};
		}

		if (props.upload && props.upload.tag === 'uploaded') {
			return {
				filename: props.upload.upload.filename,
				contentType: props.upload.upload.contentType,
				download: props.downloadable,
				delete: !props.readonly
			};
		}

		if (props.upload) {
			let errorMessage;
			if (props.upload.tag === 'validationError') {
				errorMessage = props.upload?.violationMessage;
			}
			if (props.upload.tag === 'httpError') {
				errorMessage = props.upload?.statusText;
			}
			if (props.upload.tag === 'error') {
				errorMessage = props.upload?.error;
			}

			return {
				filename: props.upload.filename,
				contentType: undefined,
				download: props.downloadable,
				delete: !props.readonly,
				error: errorMessage
			};
		}

		return {
			filename: 'Onbekend bestand',
			contentType: undefined,
			download: false,
			delete: false,
			error: 'Onbekende fout'
		};
	});
</script>

<style scoped>
	.attachment {
		max-width: 100%;
		display: flex;
		flex-direction: row;
		border: 1px solid var(--color-grey-100);
		border-radius: 5px;
		align-items: center;
		padding-left: 10px;
		--icon-size: 20px;

		.file-icon {
			flex: 0 0 20px;
			--icon-size: 20px;
		}
	}

	.attachment-title {
		display: inline-block;
		flex: 0 1 auto;
		padding: 10px;
		color: inherit;
		font-size: var(--font-size-p-2);
		line-height: var(--line-height-p-2);
		font-weight: normal;
		text-decoration: none;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	a.attachment-title:hover {
		color: var(--color-primary);
		text-decoration: underline;
	}

	.attachment button,
	.attachment .info {
		all: unset;
		flex: 0 0 auto;
		margin-left: auto;
		padding: 10px;
		cursor: pointer;
	}

	.attachment button:disabled {
		color: var(--color-grey-200);
	}

	.attachment button:not(:disabled):hover {
		color: var(--color-primary);
	}

	.status {
		display: flex;
		flex: 1 1 auto;
		justify-content: center;
		align-items: center;
		min-width: 50px;

		.bar {
			display: flex;
			flex-grow: 1;
			background-color: #d9d9d9;
			height: 3px;
			border-radius: 1px;
			margin: 0 10px;

			&:before {
				content: ' ';
				display: block;
				width: 20%;
				height: 3px;
				margin: 0;
				background-color: var(--color-success);
				transition: width .2s ease-in-out;
			}
		}

		&.status-progress {
			.bar {
				visibility: visible;

				&:before {
					width: 40%;
					animation: progress 10s ease-out;
				}
			}
		}

		&.status-completed {
			animation: fade-out 3s ease-out;
			animation-fill-mode: forwards;
			visibility: hidden;
			transition: visibility 3s ease-out;

			.bar {
				&:before {
					width: 100%;
				}
			}
		}
	}

	.error {
		color: var(--color-error);
		--icon-size: 15px;
		flex: 1 0 auto;
		max-width: 50%;
	}

	.info {
		--icon-color: var(--color-error);
	}

	@keyframes progress {
		0% {
			width: 0;
		}
		70% {
			width: 30%;
		}
		100% {
			width: 40%;
		}
	}

	@keyframes fade-out {
		0% {
			opacity: 1;
		}
		100% {
			opacity: 0;
		}
	}
</style>
